import React from 'react';
import { useTranslation } from 'react-i18next';
import { capitalise } from '../../utils/capitalise';
import { getSeverity, SeverityLevel } from './severity-levels';
import { SeverityMeter } from './severity-meter';
import './severity.css';

export type { SeverityLevel } from './severity-levels';

export type SeverityProps = {
  /**
   * Severity level to be displayed e.g. 'CRITICAL' | 'MEDIUM' | 'HIGH' | 'LOW' | 'INFORMATIONAL' | 'UNKNOWN'
   * Defaults to UNKNOWN when undefined
   */
  severityLevel?: SeverityLevel;
  /**
   * Optional - Number to be shown with the severity level
   */
  count?: number;
};

/**
 * A severity chip: a four-bar meter filled to the level, then its name, in
 * the level's colours. Screen readers hear "Severity: High".
 *
 * Example:
 * ```
 *   <Severity severityLevel="CRITICAL" />
 * ```
 */
export const Severity = ({ count, severityLevel }: SeverityProps) => {
  const { t } = useTranslation('designSystem');
  const severity = getSeverity(severityLevel);
  const isUnknownValue =
    !!severityLevel &&
    severity.key === 'unknown' &&
    severityLevel.toLowerCase() !== 'unknown';
  const level = (
    <>
      <SeverityMeter severityLevel={severity.key} />
      <span className="sr-only">{t('severityPrefix')}</span>
      <span className="severity--level" data-testid="severity-level">
        {/* A value it doesn't know keeps its own name, in unknown's colours. */}
        {isUnknownValue ? capitalise(severityLevel) : t(severity.labelKey)}
      </span>
    </>
  );

  if (typeof count === 'number') {
    return (
      <span className={`severity ${severity.className}`}>
        <span className="severity--level-icon">{level}</span>
        <span className="severity--count">{count}</span>
      </span>
    );
  }
  return (
    <span className={`severity ${severity.className}`} data-testid="severity">
      {level}
    </span>
  );
};
